<script setup lang="ts">
import {computed, type ComputedRef} from "vue";
import {useI18n} from "vue-i18n";
import {useRoute} from "vue-router";
import {deliveryD8Str} from "@/pages/RecoveryUpdates/utils/rcvyUpdtsUtils.ts";
import {useLocalizationStore} from "@/store";

const props = defineProps({
  deliveryDate: String,
  eocId: Number,
  index: {
    type: Number,
    default: 0
  },
  sumId: {
    type: Number,
    required: true,
  },
  title: String,
  viewedDate: [String, null],
});

const route = useRoute(),
  {t} = useI18n();

const {locale} = useLocalizationStore();

const cardBkgdImgUrl: ComputedRef<string> = computed(() => `url(/img/rcvy-updts-card-bkgd-${props.index % 4}.svg)`);
</script>

<template>
  <router-link class="column pat-sum-card"
               :to="{ name: 'RecoveryUpdates.Detail', params: { eocId: props.eocId, sumId: props.sumId }, state: { from: route.name?.toString() } }">
    
    <!-- Heading -->
    <div class="row items-start justify-between pat-sum-card__heading">
      
      <!-- Read/Unread -->
      <div class="col-auto pat-sum-card__read-unread">
        <template v-if="props.viewedDate !== undefined">
          
          <!-- Read -->
          <q-icon v-if="props.viewedDate"
                  color="content-txt-ttl"
                  name="fa-solid fa-check"
                  size="14px" />
  
          <!-- Unread -->
          <div v-else
               class="pat-sum-card__unread">
            <q-icon name="fa-solid fa-circle" size="5px" />{{ t('patient_dashboard_unread') }}
          </div>
        </template>
      </div>

      <!-- View Details -->
      <div class="col-auto">
        <div class="pat-sum-card__details">
          <q-icon color="primitives-foundation-100"
                  name="fa-light fa-arrow-right"
                  size="16px" />
        </div>
      </div>
    </div>
    
    <!-- Delivery Date -->
    <p v-if="props.deliveryDate"
       class="pat-sum-card__delivery">{{ deliveryD8Str(props.deliveryDate, locale, t) }}</p>
    
    <!-- Preview -->
    <p v-if="props.title"
       class="ellipsis-2-lines pat-sum-card__preview">{{ props.title }}</p>
  </router-link>
</template>

<style scoped lang="scss">
@use 'sass:map';

.pat-sum-card { // .column
  $view-details-btn-size: 32px;
  
  width: 362px;
  height: 160px;
  background-color: $white;
  background-image: v-bind(cardBkgdImgUrl);
  background-size: cover;
  background-repeat: no-repeat;
  overflow: hidden;
  padding: 20px;
  border-radius: $generic-border-radius;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.03), 0 2px 3px -2px rgba(0, 0, 0, 0.08), 0 3px 9px -3px rgba(0, 0, 0, 0.08), 0 2px 6px -2px rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.08);
  
  &:hover:not(.q-btn):not(.q-item):not(.q-tab) {
    text-decoration: none;
  }
  
  // Heading
  &__heading { // .row
    max-width: 100%;
    margin-block-end: auto;
  }

  &__read-unread { // .col-auto
    font-size: 0;
  }
  
  // Unread
  &__unread {
    background-color: $primitives-action-10;
    font-weight: map.get($text-weights, semibold);
    font-size: 12px;
    line-height: 14px;
    color: $content-trg-primary-def;
    padding: 8px;
    border-radius: 15px;
    
    .q-icon {
      margin-inline-end: 4px;
    }
  }
  
  // View Details
  &__details { // div
    display: flex;
    width: $view-details-btn-size;
    height: $view-details-btn-size;
    background-color: map.get($co-brand-clrs, primitives-foundation-10);
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  // Delivery Time & Preview
  &__delivery,
  &__preview { // p
    color: map.get($co-brand-clrs, content-txt-ttl);
    padding-inline-end: $view-details-btn-size;
  }
  
  // Delivery Time
  &__delivery { // p
    font-size: 14px;
    line-height: 18px;
    margin-block-end: 4px;
  }

  // Preview
  &__preview { // p
    font-weight: map.get($text-weights, semibold);
    font-size: 18px;
    line-height: 22px;
    text-wrap-style: balance;
  }
}
</style>